import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'react-feather';
import ChargesFieldset from './ChargesFieldset';

/**
 * Tenancy + charges section — used by AddTenant and (when
 * manageLeaseDetails permission is present) EditTenant.
 *
 * `canOverrideLicense` controls whether the license-override checkbox
 * is offered (admin / Backend User / sadmin only).
 */
const TenancySection = ({
    form,
    errors = {},
    onChange,
    mode = 'add',
    lookups,
    canOverrideLicense = false,
}) => {
    const { t } = useTranslation();
    const v = (name) => form[name] ?? '';

    return (
        <section className="v3-card v3-tenant-section" id="section-tenancy">
            <header className="v3-tenant-section-head">
                <h3>{t('Tenancy & charges')}</h3>
                <span className="v3-label">{mode === 'add' ? t('Step 3 of 3') : t('Lease & financial')}</span>
            </header>

            <div className="v3-sadmin-form-grid">
                <label className="v3-field">
                    <span className="v3-label">{t('Lease start date *')}</span>
                    <input
                        className="v3-input"
                        type="date"
                        value={v('tenant_lease_startdate')}
                        onChange={(e) => onChange('tenant_lease_startdate', e.target.value)}
                    />
                    {errors.tenant_lease_startdate && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_lease_startdate}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Lease end date *')}</span>
                    <input
                        className="v3-input"
                        type="date"
                        value={v('tenant_lease_enddate')}
                        onChange={(e) => onChange('tenant_lease_enddate', e.target.value)}
                    />
                    {errors.tenant_lease_enddate && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_lease_enddate}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Base rent *')}</span>
                    <input
                        className="v3-input"
                        type="number"
                        step="0.001"
                        value={v('tenant_base_rent')}
                        onChange={(e) => onChange('tenant_base_rent', e.target.value)}
                        placeholder="0"
                    />
                    {errors.tenant_base_rent && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_base_rent}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Revenue share % *')}</span>
                    <input
                        className="v3-input"
                        type="number"
                        step="0.001"
                        value={v('tenant_revenue_share')}
                        onChange={(e) => onChange('tenant_revenue_share', e.target.value)}
                        placeholder="0"
                    />
                    {errors.tenant_revenue_share && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_revenue_share}</span>
                    )}
                </label>

                <label className="v3-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="v3-label">{t('Revenue share method *')}</span>
                    <div className="v3-select-wrap">
                        <select
                            className="v3-input v3-select"
                            value={v('tenant_revenue_share_method_id')}
                            onChange={(e) => onChange('tenant_revenue_share_method_id', e.target.value)}
                        >
                            <option value="">{t('Select method…')}</option>
                            {(lookups?.revenueMethods || []).map((m) => (
                                <option key={m._id} value={m.revenue_method_id?._id}>
                                    {m.revenue_method_id?.description}
                                </option>
                            ))}
                        </select>
                    </div>
                    {errors.tenant_revenue_share_method_id && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_revenue_share_method_id}</span>
                    )}
                </label>

                <ChargesFieldset
                    charges={lookups?.charges || []}
                    values={form}
                    errors={errors}
                    onChange={onChange}
                />

                <label className="v3-field">
                    <span className="v3-label">{t('Contact person *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_contact_person')}
                        onChange={(e) => onChange('tenant_contact_person', e.target.value)}
                        placeholder={t('Primary contact')}
                    />
                    {errors.tenant_contact_person && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_contact_person}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Contact number *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_contact_number')}
                        onChange={(e) => onChange('tenant_contact_number', e.target.value)}
                        placeholder="+971 …"
                    />
                    {errors.tenant_contact_number && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_contact_number}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('IT contact person')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_it_contact_person')}
                        onChange={(e) => onChange('tenant_it_contact_person', e.target.value)}
                        placeholder={t('Technical contact')}
                    />
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('IT contact number')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_it_contact_number')}
                        onChange={(e) => onChange('tenant_it_contact_number', e.target.value)}
                        placeholder="+971 …"
                    />
                </label>

                <label className="v3-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="v3-label">{t('POS name *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_pos_name')}
                        onChange={(e) => onChange('tenant_pos_name', e.target.value)}
                        placeholder={t('The POS system in use')}
                    />
                    {errors.tenant_pos_name && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_pos_name}</span>
                    )}
                </label>
            </div>

            {/* License-override is admin-only and only shown on Add. */}
            {canOverrideLicense && mode === 'add' && (
                <label className="v3-sadmin-checkbox-row v3-tenant-license-override">
                    <input
                        type="checkbox"
                        checked={!!form.license_override}
                        onChange={(e) => onChange('license_override', e.target.checked)}
                    />
                    {t('Override license check (assign a license even if the pool is empty — creates a negative-balance overdraft)')}
                </label>
            )}
        </section>
    );
};

/** All fields the section owns. Used for completeness counters + validation. */
export const TENANCY_FIELDS = {
    required: [
        'tenant_lease_startdate', 'tenant_lease_enddate',
        'tenant_base_rent', 'tenant_revenue_share', 'tenant_revenue_share_method_id',
        'tenant_contact_person', 'tenant_contact_number', 'tenant_pos_name',
    ],
    all: [
        'tenant_lease_startdate', 'tenant_lease_enddate', 'tenant_base_rent',
        'tenant_revenue_share', 'tenant_revenue_share_method_id',
        'tenant_contact_person', 'tenant_contact_number',
        'tenant_it_contact_person', 'tenant_it_contact_number', 'tenant_pos_name',
    ],
};

export default TenancySection;
